<script>
import { ValidationProvider } from 'vee-validate';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import MaskedInput from 'vue-text-mask';
import PlanCustomer from '@/entity/PlanCustomer';
import { create, update } from '@/api/plan';
import { get } from 'lodash';

export default {
  name: 'PlansCustomerForm',
  components: {
    MaskedInput,
    ValidationProvider,
  },

  props: {
    plan: {
      type: PlanCustomer,
      default: () => new PlanCustomer(),
    },
  },

  data() {
    return {
      isLoading: false,
      form: this.plan,
      tiers: [
        { id: 'Pro', value: 'Pro' },
        { id: 'Self', value: 'Self' },
        { id: 'Concierge', value: 'Concierge' },
        { id: 'Brand', value: 'Brand' },
        { id: 'Free', value: 'Free' },
        { id: 'Test', value: 'Test' },
      ],
      assignedOpportunities: ['FDA', 'HELOC'],
      mask: createNumberMask({
        prefix: '',
        allowDecimal: true,
        integerLimit: 8,
        includeThousandsSeparator: false,
        allowNegative: false,
      }),
    };
  },

  computed: {
    isUpdate() { return this.form.id !== null; },

    description: {
      get() {
        return this.form.description.map((row) => `• ${row}`).join('\n');
      },
      set(newDescription) {
        this.form.description = newDescription ? newDescription.split('\n').map((row) => row.replace('• ', '')) : [];
      },
    },
  },

  methods: {
    async submit() {
      try {
        this.isLoading = true;
        const { data } = this.form.id
          ? await update({ ...this.form })
          : await create({ ...this.form });

        this.$emit('submit', { data });
        this.$emit('close');
      } catch (error) {
        const code = get(error, 'error.response.status');
        if (code < 500 || code > 599) {
          this.$buefy.snackbar.open({
            message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
            actionText: ' ',
            position: 'is-top',
            indefinite: true,
            type: 'is-danger',
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    errorMessage(field, filedRules) {
      const failedRule = Object.keys(filedRules).at(0);
      if (failedRule === 'max' || failedRule === 'min') {
        return `${field} is not valid`;
      }

      if (failedRule === 'required') {
        return 'This field is required ';
      }
      return '';
    },
  },

};
</script>

<template>
  <validation-observer v-slot="{ handleSubmit }" tag="div" class="modern-card modal-card">
      <b-loading :is-full-page="false" :active="isLoading" :can-cancel="false"></b-loading>
      <header class="modern-card__header">{{ isUpdate ? 'Edit plan' : 'New plan' }}
        <b-button class="modern-card__close-button" aria-label="close" icon-right="close"
                  @click="$emit('close')" />
      </header>
      <section class="modern-card__body">
        <form class="modern-modal-form">
          <validation-provider v-slot="{ errors, failedRules }" rules="required|max:250">
            <b-field label="Plan"
                     :type="{ 'is-danger': errors.length }"
                     :message="errorMessage('Plan', failedRules)">
              <b-input size="is-medium" v-model="form.name"></b-input>
            </b-field>
          </validation-provider>
          <validation-provider v-slot="{ errors }"
                               name="Price"
                               :rules="{ required: true, regex: /^[0-9]+([\\.][0-9]+)?$/}">
            <b-field label="Price"
                     :type="{ 'is-danger': errors.length }"
                     :message="errors" >
              <div class="control" :class="{'has-icons-right': errors.length}">
                <masked-input class="input" type="text"
                              size="is-medium"
                              v-model="form.price"
                              :mask="mask"
                              :class="{'is-danger': errors.length , 'is-medium': true}"
                              :guide="false">
                </masked-input>
                <span v-if="errors.length" class="icon is-right has-text-danger">
                    <i class="mdi mdi-alert-circle mdi-24px"></i>
                  </span>
              </div>
            </b-field>
          </validation-provider>
          <validation-provider v-slot="{ errors, failedRules }"
                               name="StripeId" rules="required|max:250">
            <b-field
                     label="Stripe ID"
                     :type="{ 'is-danger': errors.length }"
                     :message="errorMessage('Stripe ID', failedRules)">
              <b-input size="is-medium" v-model="form.stripeId"></b-input>
            </b-field>
          </validation-provider>
          <validation-provider v-slot="{ errors, failedRules }" rules="max:250">
            <b-field label="Associated Stripe ID"
                     :type="{ 'is-danger': errors.length }"
                     :message="errorMessage('Associated Stripe ID', failedRules)">
              <b-input size="is-medium" v-model="form.associatedStripeId"></b-input>
            </b-field>
          </validation-provider>
          <validation-provider v-slot="{ errors }" rules="required">
            <b-field label="Installment Intervals Count"
                     :type="{ 'is-danger': errors.length }"
                     :message="errors">
              <b-input size="is-medium" v-model="form.installmentIntervalsCount"></b-input>
            </b-field>
          </validation-provider>
          <validation-provider v-slot="{ errors }">
            <b-field label="Opportunities"
                     :type="{ 'is-danger': errors.length }"
                     :message="errors">
              <b-taginput
                size="is-medium"
                v-model="form.opportunities"
                :data="assignedOpportunities"
                :allow-duplicates="false"
                :allow-new="false"
                :open-on-focus="true"
                autocomplete></b-taginput>
            </b-field>
          </validation-provider>
          <validation-provider  rules="required" v-slot="{ errors }">
            <b-field label="Tier"
                     :type="{ 'is-danger': errors.length }"
                     :message="errors">
              <b-select size="is-medium" v-model="form.tier" expanded>
                <option value="" disabled selected></option>
                <option v-for="tier in tiers" v-bind:key="tier.id" :value="tier.value">
                  {{ tier.value }}
                </option>
              </b-select>
            </b-field>
          </validation-provider>
          <validation-provider v-slot="{ errors }">
            <b-field label="Description"
                     :type="{ 'is-danger': errors.length }"
                     :message="errors">
              <b-input size="is-medium"
                       name="description"
                       type="textarea" rows="3"
                       v-model="description"
                       ref="descr"></b-input>
            </b-field>
          </validation-provider>
        </form>
      </section>
      <footer class="modern-card__footer">
          <button type="button" class="button is-success outlined"
                  @click="$emit('close')">Cancel</button>
          <button type="button" class="button is-success"
                  @click="handleSubmit(submit)">{{ isUpdate ? 'Update' : 'Create' }}</button>
        </footer>
  </validation-observer>
</template>

<style scoped lang="stylus">

</style>
